/**
 * Collection Path Configuration
 * 
 * All org-scoped collections are now stored under orgs/{orgUid}/{collection}.
 * This file provides utilities for building collection paths and defines
 * the collection types that are org-scoped vs global.
 */

/**
 * Type representing all collections that are org-scoped (nested under org)
 */
export type MigratableCollection =
  | "groups"
  | "messages"
  | "events"
  | "campingEvents"
  | "campingRegistrations"
  | "menuItems"
  | "pages"
  | "jGroupUsers"
  | "jUserGroups"
  | "emailActivationLogs"
  | "phoneActivationLogs"
  | "auditLogs";

/**
 * Get the org-scoped collection path
 * 
 * @param collection - The collection name
 * @param orgUid - The organization UID
 * @returns The org-scoped path (e.g., "orgs/ohc400/groups")
 * @throws Error if orgUid is null or undefined
 * 
 * @example
 * getCollectionPath('groups', 'ohc400') // Returns: "orgs/ohc400/groups"
 * getCollectionPath('users', 'ohc400') // Returns: "users" (global collection)
 */
export function getCollectionPath(
  collection: MigratableCollection | string,
  orgUid: string | null,
): string {
  // Special case: users and jUserOrgs are always global, not org-scoped
  if (collection === "users" || collection === "jUserOrgs") {
    return collection;
  }
  
  if (!orgUid) {
    throw new Error("Organization UID is required");
  }
  
  // All other collections are org-scoped
  return `orgs/${orgUid}/${collection}`;
}